// =============================================================================
// dom.ts — Helpers de DOM compartilhados pelas views.
// =============================================================================
import { spriteFactory, skinSpriteId } from '../sprites';
import { SHEET_CHAR_IDS, skinSheetURL } from '../charSheets';
import { DEFAULT_SKIN } from '../../../shared/skins';
import { arrowPixelIcon } from './pixelIcons';

export const $ = (id: string): HTMLElement => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`elemento não encontrado: #${id}`);
  return el;
};

export const $input = (id: string): HTMLInputElement => $(id) as HTMLInputElement;

// Contrato mínimo dos overlays de raiz única (fim de mandato, apuração vencida,
// cassino, placar da CPI, queda de conexão): cada view é dona de UM nó raiz que
// já existe no index.html e aparece/some via display. Os métodos de exibição
// ficam de fora de propósito — cada overlay tem show() com assinatura própria.
export interface Overlay {
  readonly root: HTMLElement;
  hide(): void;
  visible(): boolean;
}

// $ escopado na raiz de um overlay: procura o id só DENTRO dela, deixando
// explícito que o nó pertence àquela view (o erro cita a raiz pra achar o
// markup certo no index.html). Ids compartilhados entre módulos seguem no $().
export const $in = (root: HTMLElement) => (id: string): HTMLElement => {
  const el = root.querySelector<HTMLElement>(`#${id}`);
  if (!el) throw new Error(`elemento não encontrado: #${id} dentro de #${root.id}`);
  return el;
};

// Retrato de personagem: quem tem atlas (charSheets) vira recorte CSS do
// próprio sheet — parado de frente (col 1 da linha 0) ou andando com `walk`;
// os demais seguem no <img> procedural via data URI. Com skin escolhida
// (issue #24), o recorte usa o atlas recolorido da skin; enquanto ele não
// fica pronto, o chibi procedural da skin segura o retrato.
export const charImg = (charId: string, px = 48, walk = false, skinId: string = DEFAULT_SKIN): string => {
  if (SHEET_CHAR_IDS.includes(charId)) {
    const url = skinId === DEFAULT_SKIN ? `/assets/${charId}_sheet.png` : skinSheetURL(charId, skinId);
    if (url) {
      return `<span class="pix-sheet${walk ? ' walk' : ''}" style="width:${px}px;height:${px}px;--cell:${px}px;`
        + `background-image:url(${url});background-size:${px * 6}px ${px * 4}px;`
        + `background-position:${-px}px 0;"></span>`;
    }
  }
  return `<img class="pix" src="${spriteFactory.dataURL(skinSpriteId(charId, skinId), 4)}" style="width:${px}px" alt="" />`;
};

// "Nome, Apelido" -> { nome: 'Nome', titulo: 'Apelido' }. Desde a issue #121
// os nomes são apelidos secos ("Mito", "Molusco"...) sem vírgula: cai no
// fallback e o título fica vazio — o card mostra só o apelido.
export const splitNome = (nomeCompleto: string): { nome: string; titulo: string } => {
  const i = nomeCompleto.indexOf(',');
  if (i < 0) return { nome: nomeCompleto, titulo: '' };
  return { nome: nomeCompleto.slice(0, i).trim(), titulo: nomeCompleto.slice(i + 1).trim() };
};

export type ScreenName = 'title' | 'menu' | 'lobby' | 'game' | 'ranking' | 'eleicao' | 'conquistas';

// Paginação das listas longas (issue #122): setas pixeladas + "PÁGINA X/Y".
// Some quando cabe tudo numa página; quem pagina os itens é a view chamadora.
export function renderPager(
  el: HTMLElement, total: number, page: number, perPage: number,
  onPage: (page: number) => void,
): void {
  el.innerHTML = '';
  const pages = Math.ceil(total / perPage);
  if (pages <= 1) return;

  const seta = (dir: 'esq' | 'dir', destino: number, rotulo: string): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn-pixel btn-cinza pager-btn';
    btn.disabled = destino < 0 || destino >= pages;
    btn.setAttribute('aria-label', rotulo);
    btn.innerHTML = arrowPixelIcon(dir, 14);
    btn.onclick = () => onPage(destino);
    return btn;
  };
  el.appendChild(seta('esq', page - 1, 'página anterior'));
  const label = document.createElement('span');
  label.className = 'pager-label';
  label.textContent = `PÁGINA ${page + 1}/${pages}`;
  el.appendChild(label);
  el.appendChild(seta('dir', page + 1, 'próxima página'));
}

export function showScreen(name: ScreenName): void {
  for (const s of document.querySelectorAll('.screen')) s.classList.remove('active');
  $(`screen-${name}`).classList.add('active');
  // O vídeo do Terceiro Turno é o fundo padrão das telas de navegação
  // (abertura, mural e comitê — issue #67); na partida ele some e pausa
  // pra não gastar CPU atrás do canvas do jogo
  const video = $('title-video') as HTMLVideoElement;
  $('video-bg').style.display = name === 'game' ? 'none' : '';
  if (name === 'game') video.pause();
  else void video.play().catch(() => { /* autoplay barrado: o poster segura o fundo */ });
}
